import { useEffect, useContext, useRef, useState } from "react";
import QRCodeStyling, { CornerSquareType, DotType } from "qr-code-styling";
import { Box, Grid, Portal, Switch, Typography, useMediaQuery, useTheme } from "@mui/material";
import { MdExpand } from "react-icons/md";

import { StyledButton } from "../components";
import { purple } from "../config";
import { bungeeFont } from "../config/theme";
import { WebsocketContext } from "../contexts/websocketContext";
import WobblyTypography from "../components/WobblyTypography";
import natklokMonster from "../assets/natklokMonster.gif";

import BackgroundOrbs from "../features/BackgroundOrbs";
import HostQuizControlsWrapper from "../features/multiplayer/HostQuizControlsWrapper";
import StyledLink from "../components/StyledLink";
import useTranslatePageText from "@natklok/hooks/useTranslatePageText";

const qrCodeStylingSettings: {
  margin: number;
  imageOptions: {
    hideBackgroundDots: boolean;
    imageSize: number;
    margin: number;
  };
  dotsOptions: {
    type: DotType;
    color: string;
  };
  backgroundOptions: {
    color: string;
  };
  image: string;
  cornersSquareOptions: {
    type: CornerSquareType;
    color: string;
  };
  cornersDotOptions: {
    color: string;
  };
} = {
  margin: 0,
  imageOptions: { hideBackgroundDots: true, imageSize: 0.4, margin: 0 },
  dotsOptions: { type: "rounded", color: "#2e2c80" },
  backgroundOptions: { color: "#ffffff" },
  image:
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAFoAAABaCAYAAAA4qEECAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAPFSURBVHgB7dxNTttAFAfw/9ioabviBvUGkezgBvQG9ASFG8AFalvqnnAC6AkIJyC9QbpJgtj4CNk1lZp5nZcPCVCkNuPxeOy+3yYCiY/8NZn35jkxIIQQQgghhBBCCA8ULHS7aaIQ3SiFIyLs//MPEkZK0UiD8uk0L+CI9f+zI/P7Z5pwPZ2mGXYUwUKEODUPJzs/KQ4C6syE8nBwkB7BEQ7Z6v/ZEf9+szLTXu/rB+zIKmgoOkU5yV7kNOwTeES0SLAjq6CdrByF/b04uuOXPUoo+/MWCrN1fMeO7Fa0O4mi6C5J0kpf8i4RaAgLdQe93Lffv42v0BiUw0L9QWO5Ss663TxD4Hg123ZLQQTNuJofHqYXCJq6haVggmaRiq5McTtBoOZzfQ9LQQXNuDjW0En8ldk2bosin8FScEFz28cHmvA6Efttg4UX9EryrhM9IBxWvfNzoQa9bPt6vSyIto+ADCWFGzQjdRFG26dLrWYWdtBYtX0m7M+oiSYauJg0Bh80U4S+y2nfLuK4XBHcaETQrgZQForxOLXunZ9rRtArie+2z3aAtE2Tgmam7YtvXn8PlbEbIG1jdSmr180JdSI1ILW41hr7cRRxC5jAsdUAKfsIR/bQROYKj9lGTuNKX49uiuBGM4OuGmE2/6WdFMGNpu3RXpCiQZkB0jYS9FZutw0mQXsiQXsiQXsiQXsiQXsiQXsiQXsiQXsiQXsiQXsiQXsiQXsiQXsSTtBEfRXhlCqYnIUgiME/v4Fw+phdrr+87/WyGb95Bi0SyIp+uYonk+xSKYzQIsHu0Zr0J/NQoCWCDZrfhqWiZditEHTXMR7nI7NXX6IFgm/vJo9f+lDUR8M1oo9uQ3G0CrqOJ9304mj5WXB9zp/0h0eb4uj777piFTQXKbPCvBcp/ruklbM3HvpkvUebFXZbR0fQ1OJYqhjyk1agDJ5xcTQPQzRI6a5jPM3yOsIm6HM0qDg6ae84bPPwDR41rTg666Mn0/TM9H0DeLQ6OTZjv3Z6YOl0Fue+e2wN8vpKsuU06NEon73paP44QgHxgvMjOIdtCpWE/Uolsw4uVBL2S5UNlZp+ZHat0und+qjemuF9GZWPSc3KHq4PF/81L/NonovUcXoMibfBf11H9VB4vcKyPKq34LKUDe+XstaTt+enuWI+1z/QcrVcM+S5yLJAKlz8nOtj159SDZHV3Q1C4/puC78X+vjpKXc6s2nLu0mHcKdwHTJrxd0NVn16nCqlj8zlNes71KzuOOPuZihCCCGEEEIIIYTw7g8SlV0XHxCcxAAAAABJRU5ErkJggg==",
  cornersSquareOptions: { type: "extra-rounded", color: "#2e2c80" },
  cornersDotOptions: { color: "#2e2c80" },
};
const qrCodeStylingClass = new QRCodeStyling({ ...qrCodeStylingSettings, width: 400, height: 400 });

const LobbyWrapper: React.FC<{ requestCreateRoom?: boolean }> = ({ requestCreateRoom = false }) => {
  const { isMultiplayerHost } = useContext(WebsocketContext);
  if (isMultiplayerHost || requestCreateRoom) return <HostLobby />;
  else return <PlayerLobby />;
};

const HostLobby: React.FC = () => {
  return (
    <HostQuizControlsWrapper inLobby>
      <Box paddingX={4} width="100%">
        <JoinInformation />
        <JoinedPlayers />
      </Box>
    </HostQuizControlsWrapper>
  );
};

const JoinInformation = () => {
  const { roomCode, allowChat, handleSetAllowChat } = useContext(WebsocketContext);
  const [qrIsExpanded, setQRIsExpanded] = useState<boolean>(false);
  const [roomCodeIsExpanded, setRoomCodeIsExpanded] = useState<boolean>(false);
  const translatePageText = useTranslatePageText();

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
        <Box marginRight={8}>
          <Typography variant="h5" fontWeight="normal">
            {translatePageText("joinInformation.visitAndJoin")}{" "}
            <b>{window.location.host}/multiplayer</b>
            <br />
            {translatePageText("joinInformation.withCode")}
          </Typography>
          <Box
            display="inline-flex"
            marginY={2}
            alignItems="center"
            bgcolor="#05ACF0"
            borderRadius={2}
            color="neutral.main"
          >
            <Box paddingX={2} paddingY={1} bgcolor="#00BDFF" borderRadius={2}>
              <Typography variant="h1">{roomCode}</Typography>
            </Box>
            <Box
              display="flex"
              alignItems="center"
              fontSize="1.5rem"
              paddingX={4}
              paddingY={2}
              style={{ cursor: "pointer" }}
              onClick={() => setRoomCodeIsExpanded(true)}
            >
              <MdExpand />
              <Typography marginLeft={1} fontWeight="bold" fontSize="1.1rem">
                {translatePageText("joinInformation.expand")}
              </Typography>
            </Box>
          </Box>
          <Typography variant="h5" fontWeight="normal" component="span" display="block">
            {translatePageText("joinInformation.orUseLink")}{" "}
            <StyledLink isOnlyText>
              {window.location.host}/multiplayer/joina/{roomCode}
            </StyledLink>
          </Typography>
        </Box>
        <div>
          <Typography variant="h5" fontWeight="normal">
            {translatePageText("joinInformation.scanQR")}
          </Typography>
          <Box
            display="inline-flex"
            marginTop={2}
            alignItems="center"
            bgcolor={purple[100]}
            borderRadius={2}
            color="neutral.main"
          >
            <Box
              paddingX={!qrIsExpanded ? 2 : undefined}
              paddingY={!qrIsExpanded ? 1 : undefined}
              bgcolor="white"
              borderRadius={!qrIsExpanded ? 2 : undefined}
              display="flex"
              justifyContent="center"
              alignItems="center"
              width={qrIsExpanded ? "100%" : undefined}
              height={qrIsExpanded ? "100%" : undefined}
              position={qrIsExpanded ? "fixed" : undefined}
              left={qrIsExpanded ? 0 : undefined}
              top={qrIsExpanded ? 0 : undefined}
              zIndex={10}
            >
              <Box display="flex" flexDirection="column" alignItems="center" paddingY={1}>
                <QRCode roomCode={roomCode} isExpanded={qrIsExpanded} />
                {qrIsExpanded && (
                  <StyledButton
                    label={translatePageText("joinInformation.close")}
                    onClick={() => setQRIsExpanded(false)}
                    style={{ marginTop: 16 }}
                  />
                )}
              </Box>
            </Box>
            <Box
              display="flex"
              alignItems="center"
              fontSize="1.5rem"
              paddingX={4}
              paddingY={2}
              style={{ cursor: "pointer" }}
              onClick={() => setQRIsExpanded(true)}
            >
              <MdExpand />
              <Typography marginLeft={1} fontWeight="bold" fontSize="1.1rem">
                {translatePageText("joinInformation.expand")}
              </Typography>
            </Box>
          </Box>
        </div>
      </div>
      {roomCodeIsExpanded && (
        <Portal>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            position="fixed"
            left={0}
            top={0}
            width="100%"
            height="100%"
            bgcolor="secondary.main"
            zIndex={11}
          >
            <Box textAlign="center" maxWidth="35rem" padding={6} color="neutral.main">
              <Typography variant="h5" fontWeight="normal" component="span" display="block">
                {translatePageText("joinInformation.visitAndJoin")}{" "}
                <b>{window.location.host}/multiplayer</b>{" "}
                {translatePageText("joinInformation.withCode")}
              </Typography>
              <Typography variant="h1" display="block" component="span" fontFamily={bungeeFont}>
                {roomCode}
              </Typography>
              <Typography variant="h5" fontWeight="normal" component="span" display="block" mb={4}>
                {translatePageText("joinInformation.orUseLink")}{" "}
                <StyledLink isOnlyText dark>
                  {window.location.host}/multiplayer/joina/{roomCode}
                </StyledLink>
              </Typography>
              <StyledButton
                label={translatePageText("joinInformation.close")}
                onClick={() => setRoomCodeIsExpanded(false)}
              />
            </Box>
          </Box>
        </Portal>
      )}
    </div>
  );
};

const JoinedPlayers: React.FC = () => {
  const { players } = useContext(WebsocketContext);
  const playersExcludingHost = players.filter((player) => player.name !== "").reverse();
  const translatePageText = useTranslatePageText();

  return (
    <Grid item xs sm md marginBottom={4}>
      <Box bgcolor={purple[600]} borderRadius={2} marginTop={4}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor={purple[700]}
          padding={1}
          width="100%"
          style={{
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
        >
          <Typography variant="h6" lineHeight={1}>
            {translatePageText("joinInformation.numberOfPlayers")}
            <br />
          </Typography>
          <Typography
            variant="h2"
            marginLeft={2}
            style={{
              fontSize: 70,
              background: "linear-gradient(141.48deg, #FFCB3F 3.88%, #D0940F 92.24%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              WebkitTextStroke: "3px #976C1D",
            }}
          >
            {playersExcludingHost.length}
          </Typography>
          <Typography
            variant="h6"
            marginLeft={1}
            style={{
              background: "linear-gradient(141.48deg, #FFCB3F 3.88%, #D0940F 92.24%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              WebkitTextStroke: "1px #976C1D",
            }}
          >
            {translatePageText("joinInformation.players")}
          </Typography>
        </Box>
        <Box padding={2} paddingTop={3} style={{ overflow: "hidden", height: 300 }}>
          {playersExcludingHost.length === 0 && (
            <Box display="flex" flexDirection="column" alignItems="center">
              <img src={natklokMonster} alt="Nätklok monster" height="100" width="100" />
              <Typography variant="caption" marginBottom={2} style={{ opacity: 0.4 }}>
                {translatePageText("joinInformation.artFoundAt")}{" "}
                <a style={{ color: "white" }} href="https://lordicon.com">
                  Lordicon
                </a>
              </Typography>
              <WobblyTypography variant="h6" color="secondary.main">
                {translatePageText("joinInformation.emptyHere")}
              </WobblyTypography>
              <Typography fontWeight="bold">
                {translatePageText("joinInformation.timeToJoin")}
              </Typography>
            </Box>
          )}
          <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap" }}>
            {playersExcludingHost.map((player) => (
              <div key={player.name}>
                <WobblyTypography style={{ margin: 0, padding: 16 }} fontFamily={bungeeFont}>
                  {player.name}
                </WobblyTypography>
              </div>
            ))}
          </div>
        </Box>
      </Box>
    </Grid>
  );
};

const QRCode: React.FC<{
  roomCode: string | undefined;
  isExpanded?: boolean;
}> = ({ roomCode, isExpanded }) => {
  const theme = useTheme();
  const isSmallerThanSm = useMediaQuery(theme.breakpoints.down("sm"));
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const url = `${window.location.origin}/multiplayer/joina/${roomCode}`;

  useEffect(() => {
    if (qrCodeRef.current) {
      qrCodeStylingClass.append(qrCodeRef.current);
    }
  }, []);

  useEffect(() => {
    qrCodeStylingClass.update({
      data: url,
    });
  }, [roomCode, url]);

  useEffect(() => {
    if (qrCodeRef.current) {
      const canvas: HTMLCanvasElement = qrCodeRef.current.getElementsByTagName("canvas")[0];
      if (isExpanded) {
        if (isSmallerThanSm) {
          canvas.style.width = "300px";
          canvas.style.height = "300px";
        } else {
          canvas.style.width = "400px";
          canvas.style.height = "400px";
        }
      } else {
        if (isSmallerThanSm) {
          canvas.style.width = "100px";
          canvas.style.height = "100px";
        } else {
          canvas.style.width = "150px";
          canvas.style.height = "150px";
        }
      }
    }
  }, [isExpanded, isSmallerThanSm, roomCode]);
  return <div ref={qrCodeRef} style={{ display: "flex", alignItems: "center" }}></div>;
};

const PlayerLobby: React.FC = () => {
  const translatePageText = useTranslatePageText();

  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
      <Box textAlign="center" mt={8}>
        <Typography variant="h3" color="secondary.main">
          {translatePageText("joinInformation.gameStartsSoon")}
        </Typography>
        <Typography fontWeight="bold" marginTop={1} marginBottom={4}>
          {translatePageText("joinInformation.waitingForHost")}
        </Typography>
        <JoinedPlayers />
      </Box>
      <BackgroundOrbs style={{ zIndex: -1 }} />
    </Box>
  );
};

export default LobbyWrapper;
